import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DescriptionOutlined, DownloadOutlined, DriveFileRenameOutline, OpenInNewOutlined } from '@mui/icons-material';
import { downloadFileFromUrl } from '../../../lib/downloadFile';
import { getDocumentDownloadUrl, getDocumentViewUrl, updateDocument } from '../../../services/documentApi';

const DOCUMENT_TYPE_OPTIONS = [
  { value: 'General', label: 'General' },
  { value: 'Passport', label: 'Passport' },
  { value: 'BirthCertificate', label: 'Birth certificate' },
  { value: 'MarriageCertificate', label: 'Marriage certificate' },
  { value: 'DriverLicense', label: 'Driver license' },
  { value: 'HrLetter', label: 'HR letter' },
  { value: 'BankStatement', label: 'Bank statement' },
  { value: 'VisaPackage', label: 'Visa package' },
];

function formatDocumentType(documentType) {
  return DOCUMENT_TYPE_OPTIONS.find((option) => option.value === documentType)?.label ?? documentType ?? 'Unknown';
}

function formatMemberName(member) {
  if (!member) {
    return '';
  }

  return [member.firstName, member.middleNames, member.lastName].filter(Boolean).join(' ');
}

function formatDate(value) {
  if (!value) {
    return 'Unknown';
  }

  const parsedValue = new Date(value);
  if (Number.isNaN(parsedValue.getTime())) {
    return value;
  }

  return parsedValue.toLocaleString();
}

function DocumentDetailsDialog({ account, document, members, onClose, onSaved, open }) {
  const [dialogName, setDialogName] = useState('');
  const [dialogType, setDialogType] = useState('General');
  const [dialogViewUrl, setDialogViewUrl] = useState('');
  const [dialogDownloading, setDialogDownloading] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogSaving, setDialogSaving] = useState(false);
  const [dialogError, setDialogError] = useState('');

  const dialogMember = useMemo(
    () => members.find((memberItem) => memberItem.memberId === document?.memberId) ?? null,
    [document?.memberId, members]
  );

  useEffect(() => {
    let ignore = false;

    async function loadDocumentPreview() {
      if (!open || !document || !account?.token) {
        setDialogName(document?.fileName ?? '');
        setDialogType(document?.documentType || 'General');
        setDialogViewUrl('');
        setDialogLoading(false);
        setDialogDownloading(false);
        setDialogSaving(false);
        setDialogError('');
        return;
      }

      setDialogName(document.fileName);
      setDialogType(document.documentType || 'General');
      setDialogViewUrl('');
      setDialogError('');
      setDialogLoading(true);

      try {
        const response = await getDocumentViewUrl({
          token: account.token,
          memberId: document.memberId,
          documentId: document.documentId,
        });

        if (!ignore) {
          setDialogViewUrl(response.viewUrl ?? '');
        }
      } catch (error) {
        if (!ignore) {
          setDialogError(error.message);
        }
      } finally {
        if (!ignore) {
          setDialogLoading(false);
        }
      }
    }

    loadDocumentPreview();
    return () => {
      ignore = true;
    };
  }, [account?.token, document, open]);

  async function handleDialogSave() {
    if (!account?.token || !document || !dialogName.trim() || !dialogType) {
      return;
    }

    setDialogSaving(true);
    setDialogError('');

    try {
      const updatedDocument = await updateDocument({
        token: account.token,
        memberId: document.memberId,
        documentId: document.documentId,
        fileName: dialogName.trim(),
        documentType: dialogType,
      });

      onSaved?.(updatedDocument);
    } catch (error) {
      setDialogError(error.message);
    } finally {
      setDialogSaving(false);
    }
  }

  async function handleDialogDownload() {
    if (!account?.token || !document) {
      return;
    }

    setDialogDownloading(true);
    setDialogError('');

    try {
      const response = await getDocumentDownloadUrl({
        token: account.token,
        memberId: document.memberId,
        documentId: document.documentId,
      });

      downloadFileFromUrl(response.viewUrl, response.fileName ?? document.fileName);
    } catch (error) {
      setDialogError(error.message);
    } finally {
      setDialogDownloading(false);
    }
  }

  const dialogDocumentIsImage = document?.contentType?.startsWith('image/');
  const dialogDocumentIsPdf = document?.contentType === 'application/pdf';

  return (
    <Dialog fullWidth maxWidth="lg" onClose={dialogSaving ? undefined : onClose} open={open}>
      <DialogTitle>{document?.fileName ?? 'Document details'}</DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.6fr) minmax(320px, 0.9fr)' },
          }}
        >
          <Paper
            sx={{
              alignItems: 'center',
              bgcolor: 'background.default',
              borderRadius: 2,
              display: 'flex',
              justifyContent: 'center',
              minHeight: 420,
              overflow: 'hidden',
              p: 2,
            }}
            variant="outlined"
          >
            {dialogLoading ? (
              <CircularProgress size={28} />
            ) : dialogError ? (
              <Alert severity="error" sx={{ width: '100%' }}>{dialogError}</Alert>
            ) : dialogViewUrl && dialogDocumentIsImage ? (
              <Box
                alt={document?.fileName ?? 'Document preview'}
                component="img"
                src={dialogViewUrl}
                sx={{ display: 'block', maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
              />
            ) : dialogViewUrl && dialogDocumentIsPdf ? (
              <Box
                component="iframe"
                src={dialogViewUrl}
                sx={{ border: 0, height: 520, width: '100%' }}
                title={document?.fileName ?? 'Document PDF preview'}
              />
            ) : dialogViewUrl ? (
              <Stack alignItems="center" spacing={1.5}>
                <DescriptionOutlined color="action" fontSize="large" />
                <Typography color="text.secondary" variant="body2">
                  Preview is not supported for this file type.
                </Typography>
                <Button
                  href={dialogViewUrl}
                  rel="noreferrer"
                  startIcon={<OpenInNewOutlined />}
                  target="_blank"
                  variant="outlined"
                >
                  Open document
                </Button>
              </Stack>
            ) : (
              <Typography color="text.secondary" variant="body2">
                Preview unavailable.
              </Typography>
            )}
          </Paper>

          <Stack spacing={2.5}>
            <TextField
              fullWidth
              label="Document name"
              onChange={(event) => setDialogName(event.target.value)}
              value={dialogName}
            />

            <TextField
              fullWidth
              label="Document type"
              onChange={(event) => setDialogType(event.target.value)}
              select
              value={dialogType}
            >
              {DOCUMENT_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <Paper sx={{ p: 2 }} variant="outlined">
              <Stack spacing={1}>
                <Typography variant="subtitle2">Metadata</Typography>
                <Typography color="text.secondary" variant="body2">
                  Family member: {dialogMember ? formatMemberName(dialogMember) : 'Unknown'}
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Uploaded at: {formatDate(document?.createdAtUtc)}
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Last updated: {formatDate(document?.updatedAtUtc)}
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Type: {formatDocumentType(document?.documentType)}
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Content type: {document?.contentType ?? 'Unknown'}
                </Typography>
              </Stack>
            </Paper>

            {dialogType === 'Passport' ? (
              <Alert severity="info">
                Deleting this passport file removes it from the documents workspace, but keeps the passport details saved on the family profile.
              </Alert>
            ) : null}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button disabled={dialogSaving || dialogDownloading} onClick={onClose}>Close</Button>
        <Button
          disabled={dialogSaving || dialogDownloading || !document}
          onClick={handleDialogDownload}
          startIcon={<DownloadOutlined />}
          variant="outlined"
        >
          {dialogDownloading ? 'Preparing...' : 'Download'}
        </Button>
        <Button
          disabled={dialogSaving || dialogDownloading || !dialogName.trim() || !dialogType}
          onClick={handleDialogSave}
          startIcon={<DriveFileRenameOutline />}
          variant="contained"
        >
          {dialogSaving ? 'Saving...' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DocumentDetailsDialog;
export { formatDocumentType };
